import type { InjectionKey, MaybeRef, Ref } from "vue";

import { useQuery } from "@pinia/colada";
import { injectLocal, provideLocal, until } from "@vueuse/core";
import { crush, isString } from "radash";
import { objectToCamel, objectToSnake } from "ts-case-convert";
import { computed, ref, toRef } from "vue";

import { getAbTestFeature } from "@solvari/ab-testing";
import {
  dayjs,
  getEnv,
  getSentry,
  isError,
  kyNoCredentials,
  returnError,
} from "@solvari/utils";

import type { LocaleMorpheus } from "@/enums/locales.enum.ts";

type Paths<Obj, Prefix extends string = ""> = {
  [Key in keyof Obj]: Obj[Key] extends string
    ? `${Prefix}${Key & string}`
    : Paths<Obj[Key], `${Prefix}${Key & string}.`>;
}[keyof Obj];

const i18nLocaleInjectKey = Symbol() as InjectionKey<Ref<LocaleMorpheus>>;

function getGlobalI18nLocale() {
  return ref(getEnv().config.localeCode);
}

/**
 * Creates a new scoped i18n locale
 */
function provideI18nLocale(
  locale: Ref<LocaleMorpheus> = getGlobalI18nLocale(),
) {
  provideLocal(i18nLocaleInjectKey, locale);
}

/**
 * Injects and returns the i18n locale ref for the current scope.
 * You need to call this synchronously in your setup function
 */
function injectI18nLocale() {
  return injectLocal(i18nLocaleInjectKey, getGlobalI18nLocale());
}

function useI18nLocaleInjection(instanceLocale?: Ref<LocaleMorpheus>) {
  if (instanceLocale) {
    return instanceLocale;
  }

  const injectLocale = returnError(() =>
    injectLocal(i18nLocaleInjectKey, null),
  )();

  if (isError(injectLocale)) {
    // This call could not be associated with the component tree
    // This may or may not be a problem, but it could be unexpected
    console.log(
      new Error("Could not inject scoped locale, using global locale"),
    );
    return getGlobalI18nLocale();
  }

  if (injectLocale) {
    return injectLocale;
  }

  const fallbackLocale = getGlobalI18nLocale();
  provideI18nLocale(fallbackLocale);
  return fallbackLocale;
}

type I18nOptions = {
  baseParams?: MaybeRef<I18nParams>;
  locale?: Ref<LocaleMorpheus>;
};

type I18nParams = Record<string, number | string>;

type I18nInstance<Keys extends string> = {
  interpolateTranslation: (translation: string, params?: I18nParams) => string;
  tr: (key: Keys, params?: I18nParams) => string;
  trAsync: (key: Keys, params?: I18nParams) => Promise<string>;
  trRef: (key: Keys, params?: I18nParams) => Readonly<Ref<string>>;
};

type I18nFactory<Keys extends string> = (
  options?: I18nOptions,
) => I18nInstance<Keys>;

/**
 * Defines an i18n utility for handling translations. It loads a specific file and adds autocompletion for its keys.
 * You need to call this synchronously in your setup function
 *
 * @param domain A unique key used to identify and cache the translation data.
 * @param loader A function that loads the message schema for a specific locale.
 */
function defineI18n<MessageSchema, Keys extends string = Paths<MessageSchema>>(
  domain: string,
  loader: (locale: LocaleMorpheus) => MessageSchema | Promise<MessageSchema>,
): I18nFactory<Keys> {
  return (options?: I18nOptions) => {
    const locale = useI18nLocaleInjection(options?.locale);

    const { state } = useQuery({
      key: () => ["translation", locale.value, domain],
      query: async () => loader(locale.value),
      staleTime: Infinity,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
    });

    type FlatTranslations = Record<Keys, string>;

    const abTestingOverrides =
      getAbTestFeature<Record<LocaleMorpheus, FlatTranslations>>(
        "translations",
      );

    const flatTranslations = computed<FlatTranslations | undefined>(() => {
      return state.value.data
        ? (crush(state.value.data) as FlatTranslations)
        : undefined;
    });

    const translations = computed<FlatTranslations | undefined>(() => {
      const overridesForLocale = abTestingOverrides.value?.[locale.value];
      if (!overridesForLocale) {
        return flatTranslations.value;
      }
      return { ...flatTranslations.value, ...overridesForLocale };
    });

    function translationError(message: string) {
      if (getEnv().config.envType === "testing") {
        return;
      }
      getSentry().captureException(new Error(`Translation Error: ${message}`));
    }

    const baseParams = ref(options?.baseParams);

    function mergeWithBaseParams(params?: I18nParams): I18nParams | undefined {
      if (!params && !baseParams.value) return undefined;
      if (params && !baseParams.value) return params;
      if (!params && baseParams.value) return baseParams.value;
      return { ...params, ...baseParams.value };
    }

    /**
     * Returns a translation for a given key.
     * Warning: This is not reactive on its own, you need to wrap it in a reactive callback yourself.
     * Will autoload the translation on first usage.
     * Will return an empty string while the translation is pending.
     * Will return the key if the loading fails.
     * Applies params to the translation, accepts any casing.
     */
    function tr(key: Keys, params?: I18nParams): string {
      if (state.value.status === "pending") {
        return "";
      }
      if (state.value.status === "error") {
        return key;
      }

      const translation = translations.value![key];

      if (!isString(translation)) {
        translationError(`Translation for ${key} not found in ${domain}`);
        return key;
      }

      if (!translation) {
        translationError(`Translation for ${key} was empty in ${domain}`);
        return key;
      }

      const mergedParams = mergeWithBaseParams(params);

      if (!mergedParams) {
        return translation;
      }

      const allCasesParams = {
        ...mergedParams,
        ...objectToSnake(mergedParams),
        ...objectToCamel(mergedParams),
      };

      return Object.entries(allCasesParams).reduce(
        (translation, [property, value]) => {
          return translation.replaceAll(`{${property}}`, String(value));
        },
        translation,
      );
    }

    /**
     * Does the same as tr but returns a Ref<string>
     */
    function trRef(key: Keys, params?: I18nParams): Readonly<Ref<string>> {
      return toRef(() => tr(key, params));
    }

    /**
     * Does the same as tr but returns a Promise that resolves when the translations are loaded
     */
    async function trAsync(key: Keys, params?: I18nParams): Promise<string> {
      await until(() => state.value.status !== "pending").toBeTruthy();
      return tr(key, params);
    }

    function interpolateTranslation(
      translation: string,
      params?: I18nParams,
    ): string {
      const mergedParams = mergeWithBaseParams(params);

      if (!mergedParams) {
        return translation;
      }

      return Object.entries(mergedParams).reduce(
        (result, [key, value]) => result.replaceAll(`{${key}}`, String(value)),
        translation,
      );
    }

    return { interpolateTranslation, tr, trRef, trAsync };
  };
}

function defineI18nMorpheus<Translations>(fileName: string) {
  return defineI18n(fileName, (locale) => {
    const timestamp =
      getEnv().config.translationsTimestamp ?? dayjs().startOf("hour").unix();

    const path = `${getEnv().network.static}/storage/translations/${timestamp}/${locale}`;

    return kyNoCredentials
      .get(`${path}/${fileName}`, {
        timeout: 3000,
        retry: { retryOnTimeout: true, limit: 3 },
      })
      .json<Translations>();
  });
}

export type { I18nFactory, I18nInstance, I18nParams };
export { defineI18n, defineI18nMorpheus, injectI18nLocale, provideI18nLocale };
